import { useTranslation } from 'react-i18next'

import { Select } from '@/components/ui/Field'
import { Input } from '@/components/ui/Input'
import { CounterpartyPicker } from '@/features/counterparties/components/CounterpartyPicker'
import { COUNTERPARTY_CATEGORIES } from '@/features/counterparties/counterpartyList'
import { NO_PARTY, type CaseClient, type PaymentParty } from '@/features/finance/paymentParty'
import type { Payment } from '@/types/domain'

/* Whom a payment names — the ONE party control of both payment forms (S-22d2).
 * It replaced `CounterpartySelect`, which offered every client and every
 * counterparty in both directions and emitted a NAME.
 *
 * ── What is offered, by direction ──
 *
 *   · OUTBOUND: «Клиент работы — <name>» first (a refund), then the strict
 *     `CounterpartyPicker` over all six categories with «+ Добавить
 *     контрагента». No free text: the only typed value ever on screen is a
 *     stored one, kept «как введено» — the server accepts it only echoed back
 *     unchanged, and the form refuses anything else until a pick.
 *   · INBOUND: the case's client, or «Другой плательщик» — a payer typed as the
 *     bank printed it (a parent company, a bank). A directory counterparty is
 *     never an inbound party.
 *
 * ── The case's client ──
 *
 * Read off the case the payment moves against (`caseClient`), never matched by
 * name. A case whose summary does not say which client it is (`clientId` is
 * optional) gets no client row at all. A stored client who is no longer the
 * case's — the case's client changed after the payment — stays as its own row
 * «как записано», in either direction: shown, never dropped, and never
 * offered as "the case's client".
 *
 * ── What it emits ──
 *
 * A `PaymentParty` (`features/finance/paymentParty.ts`), one kind at a time;
 * `partyPayload` turns it into exactly one wire shape. */

/** A client row's value: never a counterparty UUID, never the sentinels below. */
const CLIENT_ROW = 'client:'

/** «Другой плательщик» — the inbound typed payer. */
const TYPED_ROW = 'typed'

interface ClientRow {
  id: string
  label: string
  hint: string
  client: CaseClient
  disabled?: boolean
}

export function PaymentPartyPicker({
  direction,
  value,
  onChange,
  caseClient,
  stored,
  refundRefused,
  id,
  disabled,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
}: {
  /** Decides what is offered: the party rule differs by direction. */
  direction: Payment['direction']
  value: PaymentParty
  onChange: (party: PaymentParty) => void
  /** The case's client — absent while the case loads, or when the case does
   *  not say which client it is. */
  caseClient?: CaseClient
  /** An amend's stored party, so a stored client that is no longer the
   *  case's keeps its row after the reader looks at another. */
  stored?: PaymentParty
  /** Why the case's client may not be picked (S-22f: an outbound payment
   *  already allocated to costs cannot become a refund). Set, its row is
   *  offered disabled with this sentence as its hint — unless the payment
   *  already names that client, which is no switch at all. */
  refundRefused?: string
  id?: string
  disabled?: boolean
  'aria-describedby'?: string
  'aria-invalid'?: boolean
}) {
  const { t } = useTranslation()

  const clientRows: ClientRow[] = []
  if (caseClient) {
    const refused =
      refundRefused !== undefined &&
      !(stored?.kind === 'client' && stored.id === caseClient.id)
    clientRows.push({
      id: CLIENT_ROW + caseClient.id,
      label: t('finance.payments.party.caseClient', { name: caseClient.name }),
      hint: refused ? refundRefused : t('finance.payments.counterpartyKindClient'),
      client: caseClient,
      disabled: refused,
    })
  }
  const recorded = [value, stored].find(
    (party) => party?.kind === 'client' && party.id !== '' && party.id !== caseClient?.id,
  )
  if (recorded) {
    clientRows.push({
      id: CLIENT_ROW + recorded.id,
      label: recorded.name,
      hint: t('finance.payments.party.asRecorded'),
      client: { id: recorded.id, name: recorded.name },
    })
  }

  const pickClient = (rowID: string) => {
    const row = clientRows.find((candidate) => candidate.id === rowID)
    if (row && !row.disabled) onChange({ kind: 'client', id: row.client.id, name: row.client.name })
  }
  const selectedClient = value.kind === 'client' ? CLIENT_ROW + value.id : undefined

  if (direction === 'out') {
    return (
      <CounterpartyPicker
        id={id}
        value={value.kind === 'counterparty' ? value.id : ''}
        name={value.name}
        categories={COUNTERPARTY_CATEGORIES}
        strings={{
          placeholder: t('finance.payments.counterpartyPlaceholder'),
          search: t('finance.payments.counterpartySearch'),
          asEntered: t('finance.payments.counterpartyAsEntered'),
          inactive: t('cases.legs.carrierInactive'),
          add: t('finance.payments.counterpartyAdd'),
        }}
        pinned={clientRows}
        pinnedValue={selectedClient}
        onPin={pickClient}
        /* `id === ''` is not a directory row: the stored typed name re-picked
           (kept as typed, so an exact echo still saves), or nothing at all. */
        onChange={({ id: counterpartyID, name }) =>
          onChange(
            counterpartyID !== ''
              ? { kind: 'counterparty', id: counterpartyID, name }
              : name !== ''
                ? { kind: 'typed', id: '', name }
                : NO_PARTY,
          )
        }
        disabled={disabled}
        aria-describedby={describedBy}
        aria-invalid={invalid}
      />
    )
  }

  /* Inbound: a short list — the client, perhaps a stored one, and the typed
     payer — so a plain select rather than a searchable directory. */
  const typedName = value.kind === 'typed' ? value.name : ''
  return (
    <div className="flex flex-col gap-2">
      <Select
        id={id}
        value={selectedClient ?? TYPED_ROW}
        onChange={(rowID) =>
          rowID === TYPED_ROW
            ? onChange({ kind: 'typed', id: '', name: typedName })
            : pickClient(rowID)
        }
        options={[
          ...clientRows.map((row) => ({ value: row.id, label: row.label, hint: row.hint })),
          { value: TYPED_ROW, label: t('finance.payments.party.other') },
        ]}
        disabled={disabled}
        aria-describedby={describedBy}
        aria-invalid={invalid}
      />
      {selectedClient === undefined ? (
        <Input
          aria-label={t('finance.payments.party.otherName')}
          placeholder={t('finance.payments.party.otherName')}
          value={typedName}
          onChange={(e) => onChange({ kind: 'typed', id: '', name: e.target.value })}
          disabled={disabled}
          aria-describedby={describedBy}
          aria-invalid={invalid}
        />
      ) : null}
    </div>
  )
}
